import { NO_CONSENT, onConsent, type Consent } from './consent';
import type { AnalyticsEvent, AnalyticsSink } from './analytics';

/**
 * GA4, Google Ads and the Meta pixel for the ads team (architecture.md §3). Each loads only
 * once its consent is granted (`consent.ts`, off by default) and only when its id is set;
 * events tracked before that are dropped, never replayed. No personal data: event props go
 * through `safeProps`, paths lose their enquiry ids.
 */

/* ── What may leave ──────────────────────────────────────── */

const ULIDS = /[0-9A-HJKMNP-TV-Z]{26}/g;
const HAS_ULID = /[0-9A-HJKMNP-TV-Z]{26}/;
const PERSONAL_KEY = /mail|name|first|last|phone|ref|reference|token|address|passport/i;

/** `/claim/e/01K…/items/id` → `/claim/e/:id/items/id` (an enquiry id opens nothing, but it is one visitor's). */
export function redactPath(path: string): string {
  return path.replace(ULIDS, ':id').replace(/\?.*$/, '');
}

/** Numbers, booleans and short plain strings only; nothing that looks personal. */
export function safeProps(e: AnalyticsEvent): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(e)) {
    if (k === 'name' || PERSONAL_KEY.test(k)) continue;
    if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string' && v.length <= 40 && !v.includes('@') && !HAS_ULID.test(v)) out[k] = v;
  }
  return out;
}

/* ── Script loading ──────────────────────────────────────── */

export type ScriptLoader = (src: string) => void;

export const injectScript: ScriptLoader = (src) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

type Listen = (listener: (c: Consent) => void) => () => void;

/**
 * Google Consent Mode v2 for a choice (the static site's `assets/consent.js` maps it the same
 * way). `ad_personalization` stays denied: the banner asks about measuring ads, not
 * personalising them. DECISION-NEEDED (w5-08, ads team): remarketing needs its own wording.
 */
export function consentModes(c: Consent) {
  return {
    ad_storage: c.ads ? 'granted' : 'denied',
    ad_user_data: c.ads ? 'granted' : 'denied',
    ad_personalization: 'denied',
    analytics_storage: c.analytics ? 'granted' : 'denied',
  } as const;
}

/* ── gtag: GA4 + Google Ads ──────────────────────────────── */

type Gtag = (...args: unknown[]) => void;

/** Google's own snippet: gtag.js reads the Arguments object from dataLayer, not an array. */
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

export interface GtagOptions {
  /** GA4 measurement id (`G-…`), needs `analytics` consent. */
  ga4Id?: string;
  /** Google Ads id (`AW-…`), needs `ads` consent. */
  adsId?: string;
  /** Funnel event → Ads conversion label, e.g. `{ s11_signed: 'AbC-dEf' }`. */
  adsConversions?: Record<string, string>;
  listen?: Listen;
  load?: ScriptLoader;
}

export function createGtagSink({
  ga4Id,
  adsId,
  adsConversions = {},
  listen = onConsent,
  load = injectScript,
}: GtagOptions): AnalyticsSink {
  let consent: Consent = { analytics: false, ads: false };
  let loaded = false;

  listen((c) => {
    consent = c;
    const wantGa = !!ga4Id && c.analytics;
    const wantAds = !!adsId && c.ads;
    if (!loaded && (wantGa || wantAds)) {
      loaded = true;
      window.gtag = gtag;
      // Consent Mode v2: everything denied by default, before gtag.js exists, then the choice.
      gtag('consent', 'default', consentModes(NO_CONSENT));
      gtag('consent', 'update', consentModes(c));
      gtag('js', new Date());
      if (ga4Id) gtag('config', ga4Id, { send_page_view: false });
      if (adsId) gtag('config', adsId);
      load(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent((ga4Id || adsId)!)}`);
    } else if (loaded) gtag('consent', 'update', consentModes(c));
  });

  return {
    track(e) {
      if (!loaded) return;
      if (ga4Id && consent.analytics) {
        if (e.name === 'screen_view') {
          const { screen, path } = e as { screen: string; path: string };
          gtag('event', 'page_view', { page_path: redactPath(path), page_title: screen, send_to: ga4Id });
        } else gtag('event', e.name, { ...safeProps(e), send_to: ga4Id });
      }
      const label = adsConversions[e.name];
      if (adsId && consent.ads && label) gtag('event', 'conversion', { send_to: `${adsId}/${label}` });
    },
  };
}

/* ── Meta pixel ──────────────────────────────────────────── */

/**
 * Funnel events the pixel reports as standard events. DECISION-NEEDED (w3-03, ads team):
 * which funnel steps count as conversions. A lead = the email (S7), a registration = signed.
 */
export const META_EVENTS: Record<string, string> = {
  s7_contact_saved: 'Lead',
  s11_signed: 'CompleteRegistration',
};

export interface MetaPixelOptions {
  pixelId?: string;
  listen?: Listen;
  load?: ScriptLoader;
}

export function createMetaPixelSink({
  pixelId,
  listen = onConsent,
  load = injectScript,
}: MetaPixelOptions): AnalyticsSink {
  let granted = false;
  let loaded = false;
  listen((c) => {
    granted = !!pixelId && c.ads;
    if (granted && !loaded) {
      loaded = true;
      // Meta's loader stub: queue calls until fbevents.js takes over.
      const fbq = ((...args: unknown[]) => {
        fbq.queue!.push(args);
      }) as NonNullable<Window['fbq']>;
      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = '2.0';
      window.fbq = window.fbq ?? fbq;
      window._fbq = window._fbq ?? window.fbq;
      window.fbq('consent', 'grant');
      window.fbq('init', pixelId);
      load('https://connect.facebook.net/en_US/fbevents.js');
    } else if (loaded) window.fbq?.('consent', granted ? 'grant' : 'revoke');
  });
  return {
    track(e) {
      if (!granted || !window.fbq) return;
      if (e.name === 'screen_view') window.fbq('track', 'PageView');
      else if (META_EVENTS[e.name]) window.fbq('track', META_EVENTS[e.name]);
    },
  };
}

/** `s7_contact_saved:AbC,s11_signed:dEf` → `{ s7_contact_saved: 'AbC', … }` (VITE_GOOGLE_ADS_CONVERSIONS). */
export function parseConversions(v: string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of (v ?? '').split(',')) {
    const [k, label] = pair.split(':').map((s) => s.trim());
    if (k && label) out[k] = label;
  }
  return out;
}
